import {
  DESCRIPTION_MAX_LENGTH,
  DRAFT_DESCRIPTION_MAX_LENGTH,
  MAX_ISSUES_PER_REPORT
} from "../app/constants";
import {
  createId,
  makeLocalTitle,
  nowIso,
  sanitizePlainText,
  sanitizeTitle
} from "../app/validation";
import { getDb, getCurrentSchemaVersion } from "./database";
import { projectRecordings } from "./recordingState";
import {
  buildMediaRecord,
  getPreferredMediaStorageFormat,
  prepareMediaPayload,
  shouldRetryMediaStorageAsArrayBuffer
} from "./mediaRepo";
import type {
  Issue,
  MediaStorageFormat,
  Report,
  ReportSnapshot,
  Room,
  TranscriptionQueueJob,
  TranscriptionQueueStatus
} from "./schema";

export interface CompressedPhotoInput {
  fullBlob: Blob;
  thumbBlob: Blob;
  width: number;
  height: number;
}

export interface TranscriptionResultInput {
  jobId: string;
  issueId: string;
  title?: string;
  transcript?: string;
  suggestedDescription?: string;
  description?: string;
  modelUsed?: string;
  needsManualEntry?: boolean;
}

const FINAL_QUEUE_STATUSES: ReadonlySet<TranscriptionQueueStatus> = new Set([
  "canceled",
  "superseded",
  "completed"
]);
async function touchReport(reportId: string, updatedAt = nowIso()): Promise<void> {
  const db = await getDb();
  const tx = db.transaction("reports", "readwrite");
  const report = await tx.store.get(reportId);
  if (report) {
    await tx.store.put({ ...report, updatedAt });
  }
  await tx.done;
}

function isQueueJobActive(job: TranscriptionQueueJob): boolean {
  return !FINAL_QUEUE_STATUSES.has(job.status);
}

async function deleteAudioBlobIfUnused(
  queueStore: {
    getAll: () => Promise<TranscriptionQueueJob[]>;
  },
  mediaStore: {
    delete: (key: string) => Promise<void>;
  },
  audioBlobKey: string | undefined
): Promise<void> {
  if (!audioBlobKey) {
    return;
  }
  const jobs = await queueStore.getAll();
  const stillNeeded = jobs.some((job) => job.audioBlobKey === audioBlobKey && isQueueJobActive(job));
  if (!stillNeeded) {
    await mediaStore.delete(audioBlobKey);
  }
}

export async function getActiveReport(): Promise<Report | undefined> {
  const db = await getDb();
  const reports = await db.getAll("reports");
  return reports
    .filter((report) => report.active)
    .sort((a, b) => b.updatedAt.localeCompare(a.updatedAt))[0];
}

export async function countLocalReports(): Promise<number> {
  const db = await getDb();
  return db.count("reports");
}

export async function listReports(): Promise<Report[]> {
  const db = await getDb();
  const reports = await db.getAll("reports");
  return reports.sort((a, b) => b.updatedAt.localeCompare(a.updatedAt) || a.id.localeCompare(b.id));
}

export async function setActiveReport(reportId: string): Promise<void> {
  const db = await getDb();
  const tx = db.transaction("reports", "readwrite");
  const reports = await tx.store.getAll();
  if (!reports.some((report) => report.id === reportId)) {
    await tx.done;
    throw new Error("This report is no longer on this device.");
  }
  for (const report of reports) {
    const active = report.id === reportId;
    if (report.active !== active) await tx.store.put({ ...report, active });
  }
  await tx.done;
}

export async function createReportDraft(): Promise<ReportSnapshot> {
  const db = await getDb();
  const now = nowIso();
  const report: Report = {
    id: createId("report"),
    createdAt: now,
    updatedAt: now,
    reportCreatedAt: now,
    active: true,
    schemaVersion: getCurrentSchemaVersion()
  };

  const tx = db.transaction("reports", "readwrite");
  const activeReports = (await tx.store.getAll()).filter((report) => report.active);
  await Promise.all(
    activeReports.map((activeReport) =>
      tx.objectStore("reports").put({ ...activeReport, active: false })
    )
  );
  await tx.objectStore("reports").put(report);
  await tx.done;

  return { report, rooms: [], issues: [], transcriptionJobs: [] };
}

export async function getReportSnapshot(reportId: string): Promise<ReportSnapshot | undefined> {
  const db = await getDb();
  const tx = db.transaction(["reports", "rooms", "issues", "transcriptionQueue"], "readonly");
  const [report, rooms, issues, transcriptionJobs] = await Promise.all([
    tx.objectStore("reports").get(reportId),
    tx.objectStore("rooms").index("byReportId").getAll(reportId),
    tx.objectStore("issues").index("byReportId").getAll(reportId),
    tx.objectStore("transcriptionQueue").index("byReportId").getAll(reportId)
  ]);
  await tx.done;
  if (!report) {
    return undefined;
  }
  return {
    report,
    rooms: rooms.sort((a, b) => a.sortOrder - b.sortOrder || a.name.localeCompare(b.name)),
    issues: issues.sort((a, b) => a.sortOrder - b.sortOrder || a.capturedAt.localeCompare(b.capturedAt)),
    recordings: transcriptionJobs.filter((job) => job.status !== "canceled" && job.status !== "superseded")
      .sort((a, b) => a.audioVersion - b.audioVersion || a.createdAt.localeCompare(b.createdAt)),
    transcriptionJobs: transcriptionJobs
      .filter((job) => isQueueJobActive(job))
      .sort((a, b) => a.createdAt.localeCompare(b.createdAt))
  };
}

export async function updateReportAddress(reportId: string, address: string): Promise<void> {
  const db = await getDb();
  const tx = db.transaction("reports", "readwrite");
  const report = await tx.store.get(reportId);
  if (!report) {
    return;
  }
  const clean = sanitizePlainText(address, 180);
  await tx.store.put({
    ...report,
    optionalPropertyAddress: clean || undefined,
    updatedAt: nowIso()
  });
  await tx.done;
}

export async function markReportDownloaded(reportId: string): Promise<void> {
  const db = await getDb();
  const tx = db.transaction("reports", "readwrite");
  const report = await tx.store.get(reportId);
  if (!report) {
    return;
  }
  const now = nowIso();
  await tx.store.put({ ...report, downloadedAt: now, updatedAt: now });
  await tx.done;
}

export async function addRoom(reportId: string, name: string): Promise<Room> {
  const db = await getDb();
  const rooms = await db.getAllFromIndex("rooms", "byReportId", reportId);
  const now = nowIso();
  const room: Room = {
    id: createId("room"),
    reportId,
    name: sanitizeTitle(name) || "New room",
    sortOrder: rooms.length ? Math.max(...rooms.map((item) => item.sortOrder)) + 1 : 0,
    createdAt: now,
    updatedAt: now
  };
  await db.put("rooms", room);
  await touchReport(reportId, now);
  return room;
}

export async function renameRoom(roomId: string, name: string): Promise<void> {
  const db = await getDb();
  const room = await db.get("rooms", roomId);
  if (!room) {
    return;
  }
  const now = nowIso();
  await db.put("rooms", { ...room, name: sanitizeTitle(name) || room.name, updatedAt: now });
  await touchReport(room.reportId, now);
}

export async function deleteRoom(roomId: string): Promise<void> {
  const db = await getDb();
  const tx = db.transaction(["rooms", "issues", "mediaBlobs", "transcriptionQueue", "reports"], "readwrite", { durability: "strict" });
  const room = await tx.objectStore("rooms").get(roomId);
  if (!room) {
    await tx.done;
    return;
  }
  const issues = await tx.objectStore("issues").index("byRoomId").getAll(roomId);
  await tx.objectStore("rooms").delete(roomId);
  for (const issue of issues) {
    await tx.objectStore("issues").delete(issue.id);
    const jobs = await tx.objectStore("transcriptionQueue").index("byIssueId").getAll(issue.id);
    await Promise.all(jobs.map((job) => tx.objectStore("transcriptionQueue").delete(job.jobId)));
    const media = await tx.objectStore("mediaBlobs").index("byIssueId").getAll(issue.id);
    await Promise.all(media.map((record) => tx.objectStore("mediaBlobs").delete(record.key)));
  }
  const report = await tx.objectStore("reports").get(room.reportId);
  if (report) {
    await tx.objectStore("reports").put({ ...report, updatedAt: nowIso() });
  }
  await tx.done;
}

export async function createIssueFromPhoto(
  reportId: string,
  roomId: string,
  photo: CompressedPhotoInput,
  captureId?: string
): Promise<Issue> {
  const db = await getDb();
  if (captureId) {
    const saved = await db.get("issues", captureId);
    if (saved) {
      if (saved.reportId !== reportId || saved.roomId !== roomId) throw new Error("Photo recovery belongs to another room.");
      return saved;
    }
  }
  const issueCount = (await db.getAllFromIndex("issues", "byReportId", reportId)).length;
  if (issueCount >= MAX_ISSUES_PER_REPORT) {
    throw new Error(`This record already has ${MAX_ISSUES_PER_REPORT} condition notes.`);
  }

  const roomIssues = await db.getAllFromIndex("issues", "byRoomId", roomId);
  const now = nowIso();
  const issueId = captureId ?? createId("issue");
  const photoBlobKey = createId("media_photo_full");
  const photoThumbBlobKey = createId("media_photo_thumb");
  const issue: Issue = {
    id: issueId,
    reportId,
    roomId,
    sortOrder: roomIssues.length ? Math.max(...roomIssues.map((item) => item.sortOrder)) + 1 : 0,
    createdAt: now,
    updatedAt: now,
    capturedAt: now,
    photoBlobKey,
    photoThumbBlobKey,
    photoWidth: photo.width,
    photoHeight: photo.height,
    photoSizeBytes: photo.fullBlob.size,
    thumbSizeBytes: photo.thumbBlob.size,
    audioVersion: 0,
    transcriptionStatus: "needs_note",
    userEdited: false
  };

  const preferredFormat = await getPreferredMediaStorageFormat();
  try {
    await persistIssuePhotoRecords({
      db,
      issue,
      reportId,
      roomId,
      photoBlobKey,
      photoThumbBlobKey,
      photo,
      storageFormat: preferredFormat,
      createdAt: now
    });
  } catch (error) {
    if (!shouldRetryMediaStorageAsArrayBuffer(error, preferredFormat)) {
      throw error;
    }
    await persistIssuePhotoRecords({
      db,
      issue,
      reportId,
      roomId,
      photoBlobKey,
      photoThumbBlobKey,
      photo,
      storageFormat: "array_buffer",
      createdAt: now
    });
  }
  const savedIssue = await db.get("issues", issue.id);
  if (!savedIssue) throw new Error("The photo was removed before saving completed.");
  return savedIssue;
}

async function persistIssuePhotoRecords(input: {
  db: Awaited<ReturnType<typeof getDb>>;
  issue: Issue;
  reportId: string;
  roomId: string;
  photoBlobKey: string;
  photoThumbBlobKey: string;
  photo: CompressedPhotoInput;
  storageFormat: MediaStorageFormat;
  createdAt: string;
}): Promise<void> {
  const [fullPayload, thumbPayload] = await Promise.all([
    prepareMediaPayload(input.photo.fullBlob, input.photo.fullBlob.type || "image/jpeg", input.storageFormat),
    prepareMediaPayload(input.photo.thumbBlob, input.photo.thumbBlob.type || "image/jpeg", input.storageFormat)
  ]);
  const fullRecord = buildMediaRecord({
    key: input.photoBlobKey,
    reportId: input.reportId,
    issueId: input.issue.id,
    roomId: input.roomId,
    kind: "photo_full",
    payload: fullPayload,
    createdAt: input.createdAt
  });
  const thumbRecord = buildMediaRecord({
    key: input.photoThumbBlobKey,
    reportId: input.reportId,
    issueId: input.issue.id,
    roomId: input.roomId,
    kind: "photo_thumb",
    payload: thumbPayload,
    createdAt: input.createdAt
  });

  const tx = input.db.transaction(["issues", "mediaBlobs", "rooms", "reports"], "readwrite");
  const existing = await tx.objectStore("issues").get(input.issue.id);
  if (existing) {
    await tx.done;
    if (existing.reportId !== input.reportId || existing.roomId !== input.roomId) {
      throw new Error("Photo recovery belongs to another room.");
    }
    return;
  }
  const room = await tx.objectStore("rooms").get(input.roomId);
  const parent = await tx.objectStore("reports").get(input.reportId);
  if (!room || !parent || room.reportId !== input.reportId) {
    await tx.done;
    throw new Error("The room is no longer available. Choose a room and try again.");
  }
  const count = await tx.objectStore("issues").index("byReportId").count(input.reportId);
  if (count >= MAX_ISSUES_PER_REPORT) {
    await tx.done;
    throw new Error(`This record already has ${MAX_ISSUES_PER_REPORT} condition notes.`);
  }
  await tx.objectStore("mediaBlobs").put(fullRecord);
  await tx.objectStore("mediaBlobs").put(thumbRecord);
  await tx.objectStore("issues").put(input.issue);
  if (room && !room.optionalCoverThumbBlobKey) {
    await tx.objectStore("rooms").put({
      ...room,
      optionalCoverPhotoBlobKey: input.photoBlobKey,
      optionalCoverThumbBlobKey: input.photoThumbBlobKey,
      updatedAt: input.createdAt
    });
  }
  const report = await tx.objectStore("reports").get(input.reportId);
  if (report) {
    await tx.objectStore("reports").put({ ...report, updatedAt: input.createdAt });
  }
  await tx.done;
}

export async function getIssue(issueId: string): Promise<Issue | undefined> {
  const db = await getDb();
  return db.get("issues", issueId);
}

export { saveIssueAudio, applyTranscriptionResult, claimNextDueQueueJob, markQueueJobUploading,
  markQueueJobTranscribing, markQueueJobError, scheduleQueueRetry, forceQueueRetry,
  cancelIssueVoiceNote, repairReportTranscriptionJobs } from "./recordingRepo";

export async function saveIssueText(issueId: string, title: string, description: string): Promise<void> {
  const db = await getDb();
  const tx = db.transaction(["issues", "reports", "transcriptionQueue"], "readwrite", { durability: "strict" });
  const issue = await tx.objectStore("issues").get(issueId);
  if (!issue) { await tx.done; return; }
  const cleanTitle = sanitizeTitle(title);
  const cleanDescription = sanitizePlainText(description, DRAFT_DESCRIPTION_MAX_LENGTH);
  const now = nowIso();
  let next: Issue = {
    ...issue, title: cleanTitle || undefined, description: cleanDescription || undefined,
    transcriptionStatus: issue.transcriptionStatus === "approved" ? "approved" : cleanDescription ? "needs_review" : "needs_note",
    userEdited: true, userEditedAt: now, updatedAt: now
  };
  if (issue.voiceBatchId) next = projectRecordings(next, await tx.objectStore("transcriptionQueue").index("byIssueId").getAll(issueId), now);
  await tx.objectStore("issues").put(next);
  const report = await tx.objectStore("reports").get(issue.reportId);
  if (report) await tx.objectStore("reports").put({ ...report, updatedAt: now });
  await tx.done;
}

export async function approveIssue(
  issueId: string,
  title: string,
  description: string,
  allowEmpty = false
): Promise<void> {
  const db = await getDb();
  const cleanTitle = sanitizeTitle(title) || makeLocalTitle(description);
  const cleanDescription = sanitizePlainText(description, DRAFT_DESCRIPTION_MAX_LENGTH);
  if (!allowEmpty && !cleanDescription) {
    throw new Error("Add a note before marking this condition note ready.");
  }
  if (cleanDescription.length > DESCRIPTION_MAX_LENGTH) {
    throw new Error(`Shorten this note to ${DESCRIPTION_MAX_LENGTH} characters before marking it ready.`);
  }
  const now = nowIso();
  const tx = db.transaction(["issues", "mediaBlobs", "transcriptionQueue", "reports"], "readwrite");
  const issueStore = tx.objectStore("issues");
  const mediaStore = tx.objectStore("mediaBlobs");
  const queueStore = tx.objectStore("transcriptionQueue");
  const reportStore = tx.objectStore("reports");
  const issue = await issueStore.get(issueId);
  if (!issue) { await tx.done; return; }
  const jobs = await queueStore.index("byIssueId").getAll(issueId);
  if (jobs.some(isQueueJobActive)) {
    await tx.done;
    throw new Error("Recordings are still waiting to be transcribed. You can leave this photo and keep recording offline.");
  }
  if (issue.pendingSuggestedDescription || issue.pendingAiDescription) {
    await tx.done;
    throw new Error("Review the spoken notes before marking this photo ready.");
  }
  for (const job of jobs) {
    if (job.status === "completed") {
      await queueStore.put({ ...job, reviewedAt: now, audioDeletedAt: now });
      await mediaStore.delete(job.audioBlobKey);
    }
  }
  await deleteAudioBlobIfUnused(queueStore, mediaStore, issue.audioBlobKey);
  await issueStore.put({
    ...issue,
    title: cleanTitle || "No description provided",
    description: cleanDescription || undefined,
    pendingAiDescription: undefined,
    pendingAiModelUsed: undefined,
    pendingAiTitle: undefined,
    pendingTranscript: undefined,
    pendingSuggestedDescription: undefined,
    pendingCandidateJobId: undefined,
    activeTranscriptionJobId: undefined,
    audioBlobKey: undefined,
    audioDurationMs: undefined,
    audioMimeType: undefined,
    audioSizeBytes: undefined,
    transcriptionStatus: "approved",
    transcriptionError: undefined,
    approvedAt: now,
    voiceBatchId: undefined,
    voiceBaseDescription: undefined,
    pendingRecordingCount: 0,
    userEdited: true,
    userEditedAt: now,
    updatedAt: now
  });
  const report = await reportStore.get(issue.reportId);
  if (report) {
    await reportStore.put({ ...report, updatedAt: now });
  }
  await tx.done;
}

export async function acceptPendingAiText(issueId: string, editedDescription?: string, expectedCandidate?: string): Promise<void> {
  await resolvePendingAiText(issueId, true, editedDescription, expectedCandidate);
}

export async function discardPendingAiText(issueId: string, expectedCandidate?: string): Promise<void> {
  await resolvePendingAiText(issueId, false, undefined, expectedCandidate);
}

async function resolvePendingAiText(issueId: string, accept: boolean, editedDescription?: string, expectedCandidate?: string): Promise<void> {
  const db = await getDb();
  const tx = db.transaction(["issues", "reports", "transcriptionQueue"], "readwrite", { durability: "strict" });
  const issue = await tx.objectStore("issues").get(issueId);
  if (!issue) { await tx.done; return; }
  const candidate = issue.pendingSuggestedDescription ?? issue.pendingAiDescription;
  const jobs = await tx.objectStore("transcriptionQueue").index("byIssueId").getAll(issueId);
  if (!candidate || jobs.some(isQueueJobActive) || (expectedCandidate !== undefined && expectedCandidate !== candidate)) {
    await tx.done;
    throw new Error("The spoken notes have changed. Review the latest notes and try again.");
  }
  const fullDescription = editedDescription ?? candidate;
  if (accept && fullDescription.length > DRAFT_DESCRIPTION_MAX_LENGTH) {
    await tx.done;
    throw new Error(`Shorten the combined draft to ${DRAFT_DESCRIPTION_MAX_LENGTH} characters. Each recording's transcript stays saved.`);
  }
  const now = nowIso();
  const description = accept ? sanitizePlainText(fullDescription, DRAFT_DESCRIPTION_MAX_LENGTH) : issue.description;
  for (const job of jobs) {
    if (job.status === "completed" && job.voiceBatchId === issue.voiceBatchId) {
      await tx.objectStore("transcriptionQueue").put({ ...job, reviewedAt: now });
    }
  }
  await tx.objectStore("issues").put({
    ...issue, description, pendingAiDescription: undefined, pendingSuggestedDescription: undefined,
    pendingAiTitle: undefined, pendingAiModelUsed: undefined, pendingTranscript: undefined,
    pendingCandidateJobId: undefined, transcriptionStatus: description?.trim() ? "needs_review" : "needs_note",
    userEdited: true, userEditedAt: now, updatedAt: now
  });
  const report = await tx.objectStore("reports").get(issue.reportId);
  if (report) await tx.objectStore("reports").put({ ...report, updatedAt: now });
  await tx.done;
}

export async function deleteIssue(issueId: string): Promise<void> {
  const db = await getDb();
  const issue = await db.get("issues", issueId);
  if (!issue) {
    return;
  }
  const tx = db.transaction(["issues", "mediaBlobs", "transcriptionQueue", "reports"], "readwrite");
  await tx.objectStore("issues").delete(issueId);
  const jobs = await tx.objectStore("transcriptionQueue").index("byIssueId").getAll(issueId);
  await Promise.all(jobs.map((job) => tx.objectStore("transcriptionQueue").delete(job.jobId)));
  const media = await tx.objectStore("mediaBlobs").index("byIssueId").getAll(issueId);
  await Promise.all(media.map((record) => tx.objectStore("mediaBlobs").delete(record.key)));
  const report = await tx.objectStore("reports").get(issue.reportId);
  if (report) {
    await tx.objectStore("reports").put({ ...report, updatedAt: nowIso() });
  }
  await tx.done;
}

export async function deleteReport(reportId: string): Promise<void> {
  const db = await getDb();
  const tx = db.transaction(["reports", "rooms", "issues", "mediaBlobs", "transcriptionQueue"], "readwrite");
  const [rooms, issues, media] = await Promise.all([
    tx.objectStore("rooms").index("byReportId").getAll(reportId),
    tx.objectStore("issues").index("byReportId").getAll(reportId),
    tx.objectStore("mediaBlobs").index("byReportId").getAll(reportId)
  ]);
  await tx.objectStore("reports").delete(reportId);
  await Promise.all(rooms.map((room) => tx.objectStore("rooms").delete(room.id)));
  await Promise.all(issues.map((issue) => tx.objectStore("issues").delete(issue.id)));
  const jobs = await tx.objectStore("transcriptionQueue").index("byReportId").getAll(reportId);
  await Promise.all(jobs.map((job) => tx.objectStore("transcriptionQueue").delete(job.jobId)));
  await Promise.all(media.map((record) => tx.objectStore("mediaBlobs").delete(record.key)));
  await tx.done;
}
